import { action, set } from '@ember/object';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

export default class HotelRankingRoute extends Route {
  queryParams = {
    otaId: {
      refreshModel: true,
    },
    timespan: {
      refreshModel: true,
    },
    compsetId: {
      refreshModel: true,
    },
  };

  @service access;
  @service('hotel-and-market') hotelAndMarketService;
  @service store;
  @service transition;

  onlyOTAChanged = false;

  beforeModel(transition) {
    if (!this.access.hasRanking) {
      this.transition.redirectToEnabledTab(transition);
    } else {
      this.transition.validateRankingQueryParams(transition);
    }
  }

  deactivate() {
    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (this.store.isDestroyed) {
      return;
    }
    // this.store.unloadAll('day');
    this.store.unloadAll('rankingsummary');
  }

  model(params) {
    const model = {
      rankings: [],
      periods: [],
      loading: true,
    };

    set(model, 'periods.loading', true);

    const requestOverview = {
      namespace: APIGATEWAY,
      typeKey: 'rankings/aggregated',
      hotel_id: this.hotelAndMarketService.hotel.id,
      los: this.hotelAndMarketService.hotel.defaultLosRates[0],
      granularity: params.timespan,
      compset_ids: params.compsetId,
    };

    if (this.onlyOTAChanged) {
      set(this, 'onlyOTAChanged', false);
      const rankings = this.store.peekAll('rankingsummary');
      if (rankings) {
        // Fetch all hotelinfo ids of the current active compset
        const allowedHotels = (
          this.hotelAndMarketService.activeCompset?.competitors ?? []
        ).map((competitor) => competitor.id);
        // Add the hotelinfo id from the current hotel
        allowedHotels.push(this.hotelAndMarketService.hotel?.hotelinfo?.id);
        rankings.forEach((ranking) => {
          // Check if it's the correct granularity and for the correct hotels
          if (
            ranking.granularity === params.timespan &&
            allowedHotels.indexOf(ranking.hotelinfo?.id) !== -1
          ) {
            set(model, 'rankings', [...model.rankings, ranking]);
          }
        });
      }
    }
    if (model.rankings?.length === 0) {
      this.store
        .query('rankingsummary', requestOverview)
        .then((requestedRankings) => {
          set(model, 'loading', false);
          set(model, 'rankings', [...model.rankings, ...requestedRankings]);
        });
    } else {
      set(model, 'loading', false);
    }

    const ota = this.store.peekRecord('ota', params.otaId);
    if (ota && ota.hasRanking) {
      const requestArrival = {
        namespace: APIGATEWAY,
        typeKey: 'ranking',
        ota: params.otaId,
        hotel_id: this.hotelAndMarketService.hotel.id,
        los: this.hotelAndMarketService.hotel.defaultLosRates[0],
        data: 'ranking',
        compset_ids: params.compsetId,
        meta: 'nested',
      };
      this.store.query('period', requestArrival).then((periods) => {
        set(model, 'periods', [...model.periods, ...periods]);
        set(model, 'periods.loading', false);
      });
    }

    return model;
  }

  @action
  doRefresh() {
    this.refresh();
  }

  @action
  queryParamsDidChange(addedQP, currentQP, removedQP) {
    if (
      (Object.keys(addedQP).length === 1 &&
        !isEmpty(addedQP.otaId) &&
        Object.keys(removedQP).length === 0) ||
      (Object.keys(removedQP).length === 1 &&
        !isEmpty(removedQP.otaId) &&
        Object.keys(addedQP).length === 0)
    ) {
      set(this, 'onlyOTAChanged', true);
    }
    // TODO: This call to super is within an action, and has to refer to the parent
    // class's actions to be safe. This should be refactored to call a normal method
    // on the parent class. If the parent class has not been converted to native
    // classes, it may need to be refactored as well. See
    // https: //github.com/scalvert/ember-native-class-codemod/blob/master/README.md
    // for more details.
    super.actions.queryParamsDidChange.call(this, ...arguments);
  }
}
